import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const suggestions = [
  "Can I afford a $700/month car lease?",
  "Should I pay off my Chase card or invest $500 this month?",
  "How much safe spending cash do I have left this week?",
  "Am I on track for my $25k Emergency Fund by December?",
];

export default function Coach() {
  const { user } = useAuth();
  const firstName = user?.name?.split(" ")[0] || "there";
  const [msgs, setMsgs] = useState([
    {
      role: "assistant",
      text:
        `Hi ${firstName} — I'm your AI Financial Coach, and I already have your accounts, goals, and recent spending in front of me. ` +
        `Ask me anything about your money, from "Can I afford this?" to "Why do I never have anything left at the end of the month?" ` +
        `Let's map your money together.`,
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}`);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);
  const audioRef = useRef(null);
  const spokenRef = useRef(new Set());

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs]);

  const speak = async (text) => {
    if (!text) return;
    try {
      setSpeaking(true);
      const r = await api.post("/tts", { text, voice: "coral" }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); };
      await audio.play();
    } catch { setSpeaking(false); }
  };

  const send = async (textArg) => {
    const text = textArg ?? input.trim();
    if (!text || streaming) return;
    setInput("");
    const key = `${Date.now()}_${text.slice(0, 10)}`;
    setMsgs(m => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = ""; let fullText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          try {
            const j = JSON.parse(p.slice(6));
            if (j.delta) {
              fullText += j.delta;
              setMsgs(m => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + j.delta }; return c; });
            }
            if (j.error) setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "Error: " + j.error }; return c; });
        } catch (parseErr) { console.error("coach.stream.parse:", parseErr?.message || parseErr); }
        }
      }
      if (voiceOn && fullText && !spokenRef.current.has(key)) {
        spokenRef.current.add(key);
        speak(fullText);
      }
    } catch (e) {
      setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "Something went wrong. Try again." }; return c; });
    } finally { setStreaming(false); }
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input not supported in this browser"); return; }
    if (listening) { recogRef.current?.stop(); setListening(false); return; }
    const r = new SR();
    r.lang = "en-US"; r.interimResults = false; r.continuous = false;
    r.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setInput(t); setListening(false);
      setTimeout(() => send(t), 100);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    r.start(); recogRef.current = r; setListening(true);
  };

  const toggleVoiceOut = () => {
    if (voiceOn && audioRef.current) { audioRef.current.pause(); setSpeaking(false); }
    setVoiceOn(!voiceOn);
    toast.success(!voiceOn ? "Voice on — I'll speak my responses" : "Voice off");
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="border-b border-[rgba(212,175,55,0.1)] p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B38728] flex items-center justify-center pulse-glow">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold">AfroRich AI Coach</h1>
            <p className="text-xs text-[color:var(--text-muted)]">Claude Sonnet 5 · OpenAI Voice</p>
          </div>
        </div>
        <button data-testid="voice-toggle" onClick={toggleVoiceOut}
          className={`px-4 py-2 rounded-full border transition inline-flex items-center gap-2 text-sm ${voiceOn ? "border-[color:var(--gold)] bg-[rgba(212,175,55,0.1)] text-[color:var(--gold)]" : "border-[rgba(212,175,55,0.2)] text-[color:var(--text-secondary)] hover:border-[color:var(--gold)]"}`}>
          {voiceOn ? <Volume2 className={`w-4 h-4 ${speaking ? "animate-pulse" : ""}`} /> : <VolumeX className="w-4 h-4" />}
          {voiceOn ? (speaking ? "Speaking..." : "Voice on") : "Voice off"}
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto scroll-fade p-6 space-y-4">
        {msgs.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-2xl px-5 py-3 rounded-2xl ${m.role === "user" ? "bg-[rgba(212,175,55,0.15)] border border-[rgba(212,175,55,0.3)]" : "bg-[color:var(--bg-card)] border border-[rgba(255,255,255,0.05)]"}`}>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.text || (streaming && i === msgs.length - 1 ? "..." : "")}</div>
              {m.role === "assistant" && m.text && !streaming && (
                <button data-testid={`speak-msg-${i}`} onClick={() => speak(m.text)}
                  className="mt-2 text-[10px] text-[color:var(--text-muted)] hover:text-[color:var(--gold)] inline-flex items-center gap-1 transition">
                  <Volume2 className="w-3 h-3" /> Read aloud
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {msgs.length <= 1 && (
        <div className="px-6 pb-3 grid grid-cols-2 gap-2">
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => send(s)} data-testid={`suggestion-${i}`}
              className="text-left text-xs px-4 py-3 rounded-xl border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.4)] hover:bg-[rgba(212,175,55,0.05)] transition text-[color:var(--text-secondary)]">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="p-6 border-t border-[rgba(212,175,55,0.1)]">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-3">
          <button data-testid="mic-button" type="button" onClick={toggleMic}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition ${listening ? "bg-[rgba(224,106,59,0.2)] border border-[color:var(--terracotta)] text-[color:var(--terracotta)] animate-pulse" : "bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] hover:border-[color:var(--gold)]"}`}>
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <input data-testid="ai-coach-chat-input" value={input} onChange={e => setInput(e.target.value)}
            placeholder={listening ? "Listening..." : "Ask about your money..."}
            className="flex-1 px-5 py-3 rounded-full bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none" />
          <button data-testid="ai-coach-send-button" type="submit" disabled={streaming || !input.trim()}
            className="gold-btn w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-60">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
